/**
 * A drop-down that takes more than one answer.
 *
 * It looks like the other drop-downs — a box with the answer and an arrow —
 * and opens a list with a tick box for each choice, "All" at the top and Done
 * at the foot. The box reads the one choice, or how many; nothing chosen
 * means all of them. The list stays open while boxes are ticked, so choosing
 * three months is three taps, not three trips.
 *
 * Used by: the Seasons page (SeasonPage), for months and beds.
 *
 * @module MultiSelect
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/** One choice in the list. */
export interface MultiSelectOption {
  /** What onChange hands back for it. */
  value: string;
  /** What the list shows for it. */
  label: string;
  /** What the closed box says for this choice, where the full label would not fit: "Sep 2026". */
  short?: string;
}

/** Each row's look: tall enough to tap, the same type size as the box. */
const ITEM = 'min-h-11 text-base focus:bg-muted focus:text-foreground';

/** A drop-down of tick boxes, "All" first and Done last, that stays open while ticking. */
export const MultiSelect: React.FC<{
  options: MultiSelectOption[];
  /** The chosen values; empty means all. */
  value: string[];
  onChange: (next: string[]) => void;
  /** What the box says with nothing chosen, and the first choice in the list: "All beds". */
  allLabel: string;
  /** What the box says with several chosen: "3 beds". */
  manyLabel: (count: number) => string;
  doneLabel: string;
  /** The field's name for screen readers: "Beds". */
  label: string;
  align?: 'start' | 'end';
  className?: string;
}> = ({ options, value, onChange, allLabel, manyLabel, doneLabel, label, align = 'start', className }) => {
  const chosen = new Set(value);
  const text = value.length === 0
    ? allLabel
    : value.length === 1
      ? (() => { const one = options.find((option) => option.value === value[0]); return one ? one.short ?? one.label : allLabel; })()
      : manyLabel(value.length);

  /** Ticks or unticks one choice. */
  const toggle = (option: string, on: boolean): void => {
    const next = new Set(chosen);
    if (on) next.add(option); else next.delete(option);
    // Kept in the order of the list, so "May and September" never reads backwards.
    onChange(options.map((o) => o.value).filter((v) => next.has(v)));
  };
  /** Stops a tick from closing the list, as Radix's items otherwise do. */
  const keepOpen = (event: Event): void => event.preventDefault();

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${label}: ${text}`}
          className={cn(
            'relative flex h-12 w-full min-w-0 items-center rounded-md border bg-background pl-3 pr-9 text-left text-base text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          <span className="truncate">{text}</span>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="max-h-[min(60dvh,22rem)] w-[max(var(--radix-dropdown-menu-trigger-width),13rem)] overflow-y-auto">
        <DropdownMenuCheckboxItem
          className={ITEM}
          checked={value.length === 0}
          onCheckedChange={() => onChange([])}
          onSelect={keepOpen}
        >
          {allLabel}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            className={ITEM}
            checked={chosen.has(option.value)}
            onCheckedChange={(on) => toggle(option.value, on === true)}
            onSelect={keepOpen}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="min-h-11 justify-center bg-primary text-base font-medium text-primary-foreground focus:bg-primary/90 focus:text-primary-foreground">
          {doneLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
